import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { EASE } from "./motion";

const LINKS = [
    { label: "About", id: "about", testId: "nav-link-about" },
    { label: "Platform", id: "platform", testId: "nav-link-platform" },
    { label: "Student Journey", id: "journey", testId: "nav-link-journey" },
    { label: "Institutions", id: "institutions", testId: "nav-link-institutions" },
    { label: "Contact", id: "contact", testId: "nav-link-contact" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <>
            <motion.header
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, ease: EASE, delay: 0.2 }}
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled
                        ? "border-b border-ink/10 bg-paper/85 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div
                    className={`mx-auto flex max-w-[1440px] items-center justify-between px-6 transition-[padding] duration-500 md:px-10 lg:px-16 ${
                        scrolled ? "py-3.5" : "py-6"
                    }`}
                >
                    <button
                        data-testid="nav-logo"
                        onClick={() => go("top")}
                        className="group flex items-baseline gap-1.5"
                        aria-label="QRUS — back to top"
                    >
                        <span className="font-display text-xl font-bold tracking-tight text-ink">
                            QRUS
                        </span>
                        <span className="h-1.5 w-1.5 bg-cobalt transition-colors duration-300 group-hover:bg-sky" />
                    </button>

                    <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
                        {LINKS.map((link) => (
                            <button
                                key={link.id}
                                data-testid={link.testId}
                                onClick={() => go(link.id)}
                                className="group relative text-[13px] font-medium tracking-wide text-ink/70 transition-colors duration-300 hover:text-ink"
                            >
                                {link.label}
                                <span className="absolute -bottom-1 left-0 h-px w-0 bg-cobalt transition-[width] duration-300 group-hover:w-full" />
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <a
                            data-testid="nav-signin-link"
                            href="https://qrus.ai/login"
                            className="hidden border border-ink/15 px-5 py-2.5 text-[13px] font-medium tracking-wide text-ink transition-colors duration-300 hover:border-cobalt hover:text-cobalt sm:block"
                        >
                            Sign in
                        </a>
                        <button
                            data-testid="nav-cta-partner"
                            onClick={() => go("contact")}
                            className="group hidden items-center gap-2 bg-cobalt px-5 py-2.5 text-[13px] font-medium tracking-wide text-white transition-colors duration-300 hover:bg-navy sm:flex"
                        >
                            Partner with QRUS
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                        <button
                            data-testid="mobile-menu-button"
                            onClick={() => setOpen(true)}
                            className="grid h-10 w-10 place-items-center border border-ink/15 text-ink lg:hidden"
                            aria-label="Open menu"
                        >
                            <Menu className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="blueprint-grid-dark fixed inset-0 z-[60] flex flex-col bg-navy"
                        data-testid="mobile-menu-overlay"
                    >
                        <div className="flex items-center justify-between px-6 py-5">
                            <span className="font-display text-xl font-bold tracking-tight text-white">
                                QRUS
                                <span className="ml-1.5 inline-block h-1.5 w-1.5 bg-sky align-baseline" />
                            </span>
                            <button
                                data-testid="mobile-menu-close"
                                onClick={() => setOpen(false)}
                                className="grid h-10 w-10 place-items-center border border-white/20 text-white"
                                aria-label="Close menu"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
                            {LINKS.map((link, i) => (
                                <motion.button
                                    key={link.id}
                                    data-testid={`mobile-${link.testId}`}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.6, ease: EASE, delay: 0.1 + i * 0.07 }}
                                    onClick={() => go(link.id)}
                                    className="group flex items-center justify-between border-b border-white/10 py-5 text-left"
                                >
                                    <span className="font-display text-3xl font-medium text-white transition-colors group-hover:text-sky">
                                        {link.label}
                                    </span>
                                    <span className="text-xs tracking-[0.25em] text-sky/60">
                                        0{i + 1}
                                    </span>
                                </motion.button>
                            ))}
                        </nav>
                        <div className="px-8 pb-10">
                            <button
                                data-testid="mobile-nav-cta-partner"
                                onClick={() => go("contact")}
                                className="flex w-full items-center justify-center gap-2 bg-sky px-6 py-4 text-sm font-medium tracking-wide text-navy"
                            >
                                Partner with QRUS
                                <ArrowUpRight className="h-4 w-4" />
                            </button>
                            <a
                                data-testid="mobile-nav-signin-link"
                                href="https://qrus.ai/login"
                                className="mt-3 flex w-full items-center justify-center gap-2 border border-white/25 px-6 py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:border-sky hover:text-sky"
                            >
                                Sign in to QRUS LMS
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
